// eslint-disable-next-line no-restricted-imports
import {
  Configuration,
  DefaultApi as CurrencyApi,
  DefaultApiConfirmExchangeOrderRequest,
  DefaultApiCreateExchangeOrderRequest,
  DefaultApiGetExchangeRateRequest,
  DefaultApiGetExchangeSettlementDateRequest,
  DefaultApiValidateExchangeOrderRequest,
  ExchangeMode,
  ExchangeOrderResponseResponseTypeEnum,
  ExchangeOrderTemporaryResponseResponseTypeEnum,
  ExchangeOrderValidationResponseResponseTypeEnum,
  GetExchangeRateResponse,
  GetExchangeSettlementDateResponse,
  ConfirmExchangeOrder200Response as IConfirmExchangeOrder200Response,
  ModelError as IErrorResult,
  ExchangeOrderRequest as IExchangeOrderRequest,
  ExchangeOrderTemporaryResponse as IExchangeOrderTemporaryResponse,
  ExchangeOrderValidationResponse as IExchangeOrderValidationResponse,
} from '@eih-netbroker/api-currency';
import { AxiosInstance, AxiosResponse } from 'axios';
import { IConfigAPI } from '../config';
import { ResourceHeadersWithToken } from '../services';
import { BaseResource } from './base.resource';
import { resolveResourceBasePath } from './resourceBasePath';

export type ExchangeRateRequest = Omit<DefaultApiGetExchangeRateRequest, keyof ResourceHeadersWithToken>;
export type ExchangeRateResponse = GetExchangeRateResponse;

export const CurrencyExchangeMode = ExchangeMode;
export type CurrencyExchangeMode = ExchangeMode;

export type ErrorResult = IErrorResult;

export type CurrencyExchangeSettlementDateRequest = Omit<
  DefaultApiGetExchangeSettlementDateRequest,
  keyof ResourceHeadersWithToken
>;

export type CurrencyExchangeSettlementDateResponse = GetExchangeSettlementDateResponse;

export type ExchangeOrderRequest = IExchangeOrderRequest;

export type ValidateExchangeOrderRequest = Omit<DefaultApiValidateExchangeOrderRequest, keyof ResourceHeadersWithToken>;

export type CreateExchangeOrderRequest = Omit<DefaultApiCreateExchangeOrderRequest, keyof ResourceHeadersWithToken>;

export type ExchangeOrderTemporaryResponse = IExchangeOrderTemporaryResponse;

export type ExchangeOrderValidationResponse = IExchangeOrderValidationResponse;

export type ExchangeOrderResponse = IConfirmExchangeOrder200Response;

export type ConfirmExchangeOrderRequest = Omit<DefaultApiConfirmExchangeOrderRequest, keyof ResourceHeadersWithToken>;

export type CreateExchangeOrderResponse =
  | (ExchangeOrderTemporaryResponse & {
      responseType: typeof ExchangeOrderTemporaryResponseResponseTypeEnum.Temporaryorder;
    })
  | (ExchangeOrderValidationResponse & {
      responseType: typeof ExchangeOrderValidationResponseResponseTypeEnum.Validation;
    })
  | (ExchangeOrderResponse & {
      responseType: typeof ExchangeOrderResponseResponseTypeEnum.Order;
    });

export class CurrencyResource extends BaseResource {
  // Name has to be overridden, since the build job renames it to 'r' and
  // every resource class will have the same name
  // @ts-ignore
  static readonly name = 'CurrencyResource';
  private currencyApi: CurrencyApi;

  constructor(axiosInstance: AxiosInstance, basePath: string, configApi: IConfigAPI) {
    const currencyBasePath = resolveResourceBasePath(basePath, configApi.usUrl.currency);
    super(axiosInstance, currencyBasePath, configApi);
    this.currencyApi = new CurrencyApi(
      new Configuration({
        basePath: currencyBasePath,
      }),
      currencyBasePath,
      axiosInstance,
    );
  }

  async getExchangeRate(
    request: ExchangeRateRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<ExchangeRateResponse>> {
    return this.currencyApi.getExchangeRate(
      { ...request, ...headers },
      {
        baseURL: this.basePath,
      },
    );
  }

  async getSettlementDate(
    request: CurrencyExchangeSettlementDateRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<CurrencyExchangeSettlementDateResponse>> {
    return this.currencyApi.getExchangeSettlementDate(
      { ...request, ...headers },
      {
        baseURL: this.basePath,
      },
    );
  }

  async validateExchangeOrder(
    params: ValidateExchangeOrderRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<ExchangeOrderValidationResponse>> {
    return this.currencyApi.validateExchangeOrder(
      {
        ...params,
        ...headers,
      },
      {
        baseURL: this.basePath,
      },
    );
  }

  async createExchangeOrder(
    params: CreateExchangeOrderRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<CreateExchangeOrderResponse>> {
    return this.currencyApi.createExchangeOrder(
      {
        ...params,
        ...headers,
      },
      {
        baseURL: this.basePath,
      },
    ) as Promise<AxiosResponse<CreateExchangeOrderResponse>>;
  }

  async confirmExchangeOrder(
    params: ConfirmExchangeOrderRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<CreateExchangeOrderResponse>> {
    return this.currencyApi.confirmExchangeOrder(
      {
        ...params,
        ...headers,
      },
      {
        baseURL: this.basePath,
      },
    ) as Promise<AxiosResponse<CreateExchangeOrderResponse>>;
  }
}
